import { Chart, useChart } from '@chakra-ui/charts';
import { Card, Flex, SegmentGroup, SegmentGroupValueChangeDetails, Text } from '@chakra-ui/react';
import { memo, useCallback, useState } from 'react';
import { Area, AreaChart } from 'recharts';

type TimePeriod = '1D' | '1W' | '1M' | 'ALL';

const timePeriods: TimePeriod[] = ['1D', '1W', '1M', 'ALL'];

const periodLabels: Record<TimePeriod, string> = {
  '1D': 'Past Day',
  '1W': 'Past Week',
  '1M': 'Past Month',
  ALL: 'All Time',
};

const sparklineData = [
  { value: 0 },
  { value: 12 },
  { value: 8 },
  { value: 22 },
  { value: 18 },
  { value: 30 },
  { value: 25 },
  { value: 35 },
  { value: 28 },
  { value: 40 },
  { value: 38 },
  { value: 45 },
];

interface Props {
  profitLoss: number;
}

const ProfitLossCard = ({ profitLoss }: Props) => {
  const [period, setPeriod] = useState<TimePeriod>('1M');

  const handlePeriodChange = useCallback((details: SegmentGroupValueChangeDetails) => {
    setPeriod(details.value as TimePeriod);
  }, []);

  const chart = useChart({
    data: sparklineData,
    series: [{ name: 'value', color: 'blue.400' }],
  });

  const sign = profitLoss >= 0 ? '' : '-';
  const color = profitLoss >= 0 ? 'text.primary' : 'red.600';

  return (
    <Card.Root
      borderColor="border.light"
      borderRadius="xl"
      bg="surface.background"
      flex={1}
      minW={0}
      h="full"
    >
      <Card.Body p={5} gap={3} justifyContent="space-between">
        <Flex direction="row" justifyContent="space-between" alignItems="flex-start">
          <Flex direction="column" gap={1}>
            <Flex direction="row" gap={2} alignItems="center">
              <Text fontSize="sm" color="text.secondary" fontWeight="medium">
                Profit/Loss
              </Text>
            </Flex>
            <Text fontSize="3xl" fontWeight="bold" color={color}>
              {sign}${Math.abs(profitLoss).toFixed(2)}
            </Text>
            <Text fontSize="sm" color="text.secondary">
              {periodLabels[period]}
            </Text>
          </Flex>
          <SegmentGroup.Root size="xs" value={period} onValueChange={handlePeriodChange}>
            <SegmentGroup.Indicator bg="surface.accent" />
            {timePeriods.map((p) => (
              <SegmentGroup.Item key={p} value={p}>
                <SegmentGroup.ItemText fontSize="xs" fontWeight="medium">
                  {p}
                </SegmentGroup.ItemText>
                <SegmentGroup.ItemHiddenInput />
              </SegmentGroup.Item>
            ))}
          </SegmentGroup.Root>
        </Flex>
        <Chart.Root chart={chart} aspectRatio="auto" h="60px">
          <AreaChart data={chart.data}>
            <defs>
              <linearGradient id="pnlGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={chart.color('blue.400')} stopOpacity={0.3} />
                <stop offset="100%" stopColor={chart.color('blue.400')} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            {chart.series.map((item) => (
              <Area
                key={item.name}
                type="monotone"
                isAnimationActive={false}
                dataKey={chart.key(item.name)}
                stroke={chart.color(item.color)}
                fill="url(#pnlGradient)"
                strokeWidth={2}
                dot={false}
              />
            ))}
          </AreaChart>
        </Chart.Root>
      </Card.Body>
    </Card.Root>
  );
};

export default memo(ProfitLossCard);
